/**
 * HUD projection (plan G10): float64 world → camera space → NDC → CSS pixels, for whichever camera is live
 * (chase or cockpit). The world → camera subtraction happens in float64 first (D-011), so a marker 10 km away
 * is as stable as one at 10 m. Points behind the camera or outside the viewport also get an edge position
 * and an arrow angle (0 = up, clockwise positive, radians: CSS `rotate()` order) for the off-screen arrow.
 * Every function is allocation-free (D-014).
 */
import type { Quat, Vec3 } from '../core/math'

/** Anything closer than this in front of the camera counts as behind it (m). */
export const NEAR_DEPTH = 0.05

/** The live camera, as the HUD needs it. Refilled every frame by `setHudCamera`. */
export interface HudCamera {
  /** Camera position, float64 world. */
  x: number
  y: number
  z: number
  /** Camera orientation (three.js convention: the camera looks down its local −Z, +Y up). */
  qx: number
  qy: number
  qz: number
  qw: number
  /** tan(vertical FOV / 2) ÷ zoom. */
  tanHalfFovY: number
  /** Viewport size in CSS pixels. */
  width: number
  height: number
}

export function createHudCamera(): HudCamera {
  return { x: 0, y: 0, z: 0, qx: 0, qy: 0, qz: 0, qw: 1, tanHalfFovY: Math.tan((35 * Math.PI) / 180), width: 1, height: 1 }
}

/**
 * Fills `cam` from the rig's float64 position plus the three.js camera's own local offset (shake, if any),
 * its quaternion, vertical FOV (degrees) and zoom, and the viewport size in CSS pixels.
 */
export function setHudCamera(
  cam: HudCamera,
  rigPos: Vec3,
  localOffset: Vec3,
  rot: Quat,
  fovDeg: number,
  zoom: number,
  width: number,
  height: number,
): void {
  cam.x = rigPos.x + localOffset.x
  cam.y = rigPos.y + localOffset.y
  cam.z = rigPos.z + localOffset.z
  cam.qx = rot.x
  cam.qy = rot.y
  cam.qz = rot.z
  cam.qw = rot.w
  cam.tanHalfFovY = Math.tan((fovDeg * Math.PI) / 360) / (zoom > 0 ? zoom : 1)
  cam.width = width > 0 ? width : 1
  cam.height = height > 0 ? height : 1
}

/** A projected point. Everything is in CSS pixels from the top-left of the viewport. */
export interface ScreenPoint {
  /** Screen position (valid when `inFront`). */
  x: number
  y: number
  /** Distance along the camera's view axis (m); ≤ NEAR_DEPTH when behind. */
  depth: number
  /** In front of the camera (depth > NEAR_DEPTH). */
  inFront: boolean
  /** In front and inside the viewport. */
  onScreen: boolean
  /** Direction from the screen centre toward the point, radians, 0 = up, clockwise (for the edge arrow). */
  angle: number
  /** Where the off-screen arrow goes: the ray from the centre along `angle`, clamped to the viewport inset by the margin. */
  edgeX: number
  edgeY: number
  /** Screen pixels per metre at this depth (0 when behind). */
  pxPerMeter: number
}

export function createScreenPoint(): ScreenPoint {
  return { x: 0, y: 0, depth: 0, inFront: false, onScreen: false, angle: 0, edgeX: 0, edgeY: 0, pxPerMeter: 0 }
}

/**
 * Scratch: the last camera-space vector (D-014). A typed array, not three module `let`s: a module or closure variable
 * holds a float as a boxed number, so every write of a fresh float allocated 16 B (three per projection; D-TBD heap sweep).
 */
const cam3 = new Float64Array(3)

/** World-space vector → camera space (inverse camera rotation), into the module scratch. */
function toCameraSpace(cam: HudCamera, vx: number, vy: number, vz: number): void {
  // Rotate by the conjugate quaternion: v' = v + w·t + q×t with q = −(qx, qy, qz), t = 2·(q × v).
  const qx = -cam.qx
  const qy = -cam.qy
  const qz = -cam.qz
  const qw = cam.qw
  const tx = 2 * (qy * vz - qz * vy)
  const ty = 2 * (qz * vx - qx * vz)
  const tz = 2 * (qx * vy - qy * vx)
  cam3[0] = vx + qw * tx + (qy * tz - qz * ty)
  cam3[1] = vy + qw * ty + (qz * tx - qx * tz)
  cam3[2] = vz + qw * tz + (qx * ty - qy * tx)
}

/** Projects the camera-space scratch vector into `out`. `margin` insets the edge clamp (CSS px). */
function projectCameraSpace(cam: HudCamera, margin: number, out: ScreenPoint): boolean {
  const w = cam.width
  const h = cam.height
  const cx = w * 0.5
  const cy = h * 0.5
  // Pixels per unit of (camera-space x or y ÷ depth): half the height over tan(fov/2).
  const k = cy / cam.tanHalfFovY
  const camX = cam3[0]
  const camY = cam3[1]
  const depth = -cam3[2]
  out.depth = depth
  out.inFront = depth > NEAR_DEPTH

  // Direction on screen from the centre. In front it is the projected offset; behind the camera the
  // perspective divide would mirror it, so the camera-space (x, −y) direction is used instead: it points the
  // way the player must turn. Both agree in sign in front.
  let dx = camX
  let dy = -camY
  if (dx * dx + dy * dy < 1e-12) {
    // Dead ahead or dead behind: straight ahead has no meaningful arrow, straight behind points down.
    dx = 0
    dy = out.inFront ? -1 : 1
  }
  out.angle = Math.atan2(dx, -dy)

  if (out.inFront) {
    const inv = k / depth
    out.x = cx + camX * inv
    out.y = cy - camY * inv
    out.pxPerMeter = inv
    out.onScreen = out.x >= 0 && out.x <= w && out.y >= 0 && out.y <= h
  } else {
    out.x = cx
    out.y = cy
    out.pxPerMeter = 0
    out.onScreen = false
  }

  // Edge clamp: the ray from the centre along (dx, dy) meets the inset rectangle.
  const hx = cx - margin > 1 ? cx - margin : 1
  const hy = cy - margin > 1 ? cy - margin : 1
  const ax = dx < 0 ? -dx : dx
  const ay = dy < 0 ? -dy : dy
  const tX = ax > 0 ? hx / ax : Infinity
  const tY = ay > 0 ? hy / ay : Infinity
  const t = tX < tY ? tX : tY
  out.edgeX = cx + dx * t
  out.edgeY = cy + dy * t
  return out.onScreen
}

/** Projects a float64 world point. Returns true when it is on screen. */
export function projectWorld(cam: HudCamera, wx: number, wy: number, wz: number, margin: number, out: ScreenPoint): boolean {
  toCameraSpace(cam, wx - cam.x, wy - cam.y, wz - cam.z)
  return projectCameraSpace(cam, margin, out)
}

/** Projects a world-space direction (a point at infinity, e.g. the velocity vector). Returns true when on screen. */
export function projectDirection(cam: HudCamera, dx: number, dy: number, dz: number, margin: number, out: ScreenPoint): boolean {
  toCameraSpace(cam, dx, dy, dz)
  projectCameraSpace(cam, margin, out)
  // A direction has no distance: report scale 0 so nobody sizes a marker from it.
  out.pxPerMeter = 0
  return out.onScreen
}

/**
 * The screen angle (0 = up, clockwise, radians) of a world-space direction around the screen centre: the way
 * to turn toward it. Used by the damage-direction wedges. Dead ahead is up, dead behind is down.
 */
export function directionAngle(cam: HudCamera, dx: number, dy: number, dz: number): number {
  toCameraSpace(cam, dx, dy, dz)
  const sx = cam3[0]
  const sy = -cam3[1]
  if (sx * sx + sy * sy < 1e-12) return cam3[2] < 0 ? 0 : Math.PI
  return Math.atan2(sx, -sy)
}
